import { useMemo } from "react";
import type { ActivityFilters, ActivityItem } from "@/types/activity";

interface ActivityFeedProps {
    items: ActivityItem[];
    renderItem: (item: ActivityItem) => React.ReactElement;
    filters?: ActivityFilters;
}

// Utility: group by day (yyyy-mm-dd)
const formatKey = (d: Date) => {
    const yy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yy}-${mm}-${dd}`;
};

const humanDay = (date: Date) => {
    const today = new Date();
    const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
    const key = formatKey(date);
    if (formatKey(today) === key) return "Oggi";
    if (formatKey(yesterday) === key) return "Ieri";
    return date.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

export const ActivityFeed = ({ items, renderItem, filters }: ActivityFeedProps) => {
    const filtered = useMemo(() => {
        let list = [...items];
        if (filters?.readState && filters.readState !== "all") {
            list = list.filter((i) => (filters.readState === "unread" ? !i.isRead : i.isRead));
        }
        if (filters?.fromDate) {
            list = list.filter((i) => i.timestamp >= filters.fromDate!);
        }
        if (filters?.toDate) {
            list = list.filter((i) => i.timestamp <= filters.toDate!);
        }
        if (filters?.keyword) {
            const k = filters.keyword.toLowerCase();
            list = list.filter((i) =>
                (i.title?.toLowerCase().includes(k)) ||
                (i.description?.toLowerCase().includes(k)) ||
                (i.metadata?.containerNumber?.toLowerCase().includes(k) ?? false)
            );
        }
        // sort desc by time
        list.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
        return list;
    }, [items, filters]);

    const grouped = useMemo(() => {
        const map = new Map<string, ActivityItem[]>();
        filtered.forEach((i) => {
            const key = formatKey(i.timestamp);
            const arr = map.get(key) || [];
            arr.push(i);
            map.set(key, arr);
        });
        // Keep order by date desc
        return Array.from(map.entries())
            .sort(([a], [b]) => (a > b ? -1 : 1))
            .map(([key, arr]) => ({ key, label: humanDay(arr[0].timestamp), arr }));
    }, [filtered]);

    if (filtered.length === 0) {
        return (
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary text-tertiary">
                Nessuna attività trovata con i filtri selezionati.
            </div>
        );
    }

    return (
        <div className="rounded-xl bg-primary p-0 shadow-xs ring-1 ring-secondary">
            <ul className="divide-y divide-secondary">
                {grouped.map((group) => (
                    <li key={group.key} className="p-0">
                        <div className="sticky top-0 z-10 bg-primary/80 backdrop-blur px-6 py-3 text-sm font-medium text-tertiary border-b border-secondary">
                            {group.label}
                        </div>
                        <ul className="px-6 py-4 space-y-4">
                            {group.arr.map((item) => (
                                <li key={item.id}>{renderItem(item)}</li>
                            ))}
                        </ul>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default ActivityFeed;


